import { useEffect, useMemo, useState } from 'react';
import { api } from '@/api/client';
import { ErrorNote, Modal, errorMessage } from '@/components/ui';
import { formatNumber } from '@/format';
import { useAuth } from '@/stores/auth';
import { useCompany } from '@/stores/company';
import { useFarmScope } from '@/stores/farmScope';
import { toast } from '@/stores/toast';
import {
  GROUPS,
  PHRASES,
  RESET_POST,
  WIPE_POST,
  closeOver,
  type ResetAreaDef,
  type ResetMode,
  type ResetOutcome,
  type ResetPreview,
} from './reset';

/**
 * Settings › Reset data. Clears data from inside the app, for example test
 * entries before the system goes live: chosen areas, or everything but the
 * audit log and people's logins. A Super Admin also sees a way to wipe the
 * system completely.
 */
export function ResetData() {
  const can = useAuth((s) => s.can);
  const canReset = can(RESET_POST);
  const canWipe = can(WIPE_POST);

  const [areas, setAreas] = useState<ResetAreaDef[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [preview, setPreview] = useState<{ key: string; data: ResetPreview } | null>(null);
  const [checking, setChecking] = useState<ResetMode | null>(null);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<ResetMode | null>(null);
  const [outcome, setOutcome] = useState<ResetOutcome | null>(null);

  useEffect(() => {
    let live = true;
    api
      .get('/api/v1/system/reset/areas')
      .then((a) => live && setAreas(a as ResetAreaDef[]))
      .catch((e) => live && setLoadError(errorMessage(e, 'Could not load the list of data.')));
    return () => {
      live = false;
    };
  }, []);

  const { all, pulledBy } = useMemo(
    () => closeOver(ticked, areas ?? []),
    [ticked, areas],
  );
  const partialKey = `partial:${[...all].sort().join(',')}`;
  const keyFor = (mode: ResetMode) => (mode === 'partial' ? partialKey : mode);
  const previewFor = (mode: ResetMode) =>
    preview && preview.key === keyFor(mode) ? preview.data : null;

  function toggle(id: string) {
    setTicked((t) => {
      const next = new Set(t);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function check(mode: ResetMode) {
    setChecking(mode);
    setCheckError(null);
    try {
      const data = (await api.get('/api/v1/system/reset/preview', {
        mode,
        // What was ticked; the server adds what those need, and records the
        // two apart.
        areas: mode === 'partial' ? [...ticked].sort().join(',') : undefined,
      })) as ResetPreview;
      setPreview({ key: keyFor(mode), data });
    } catch (e) {
      setCheckError(errorMessage(e, 'Could not check what would be removed.'));
    } finally {
      setChecking(null);
    }
  }

  function done(result: ResetOutcome) {
    if (result.signedOut) {
      useAuth
        .getState()
        .endSession(
          result.mode === 'complete'
            ? 'The system was reset. Sign in again with the default password.'
            : 'The system was reset. Sign in again.',
        );
      return;
    }
    setOutcome(result);
    setTicked(new Set());
    setPreview(null);
    // Farms, stores and company details may be gone; screens read them afresh.
    useFarmScope.getState().reload();
    void useCompany.getState().load();
    toast.success(`${formatNumber(result.totalRemoved, 0)} records removed.`);
  }

  if (loadError) return <ErrorNote message={loadError} />;

  const partialPreview = previewFor('partial');

  return (
    <section className="settingsapp__section" aria-labelledby="settings-reset">
      <header className="settingsapp__head">
        <div>
          <h2 id="settings-reset">Reset data</h2>
          <p className="muted settings__intro">
            Clear data you no longer want, for example test entries before the system goes live. A backup
            is taken first. Numbers and codes in what you clear start again at 1. Every reset is written
            in the audit log.
          </p>
        </div>
      </header>

      {outcome && <ResetResult outcome={outcome} onClose={() => setOutcome(null)} />}

      <div className="resetdata__panel">
        <h3 className="resetdata__title">Clear chosen data</h3>
        <p className="muted settings__hint">
          Tick what to clear. Some things cannot be cleared on their own and are ticked for you.
        </p>

        {!areas ? (
          <p className="muted">Loading…</p>
        ) : (
          <div className="resetdata__groups">
            {GROUPS.map((group) => {
              const inGroup = areas.filter((a) => a.group === group);
              if (inGroup.length === 0) return null;
              return (
                <fieldset key={group} className="resetdata__group">
                  <legend>{group}</legend>
                  {inGroup.map((a) => {
                    const pulled = pulledBy.get(a.id);
                    const count = partialPreview?.areas.find((p) => p.id === a.id)?.total;
                    return (
                      <label key={a.id} className="resetdata__area">
                        <input
                          type="checkbox"
                          checked={all.has(a.id)}
                          disabled={!canReset || pulled !== undefined}
                          onChange={() => toggle(a.id)}
                        />
                        <span>
                          <span className="resetdata__label">
                            {a.label}
                            {count !== undefined && (
                              <span className="resetdata__count"> · {formatNumber(count, 0)} records</span>
                            )}
                          </span>
                          <span className="settings__hint">{a.description}</span>
                          {pulled !== undefined && (
                            <span className="settings__hint resetdata__pulled">
                              Also cleared with {areas.find((x) => x.id === pulled)?.label ?? pulled}
                              {areas.find((x) => x.id === pulled)?.needsWhy
                                ? `, because ${areas.find((x) => x.id === pulled)!.needsWhy}.`
                                : '.'}
                            </span>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </fieldset>
              );
            })}
          </div>
        )}

        {partialPreview && <PreviewSummary preview={partialPreview} />}
        {checkError && <ErrorNote message={checkError} />}

        {canReset && (
          <div className="formactions">
            <button
              type="button"
              className="btn"
              disabled={all.size === 0 || checking !== null}
              onClick={() => void check('partial')}
            >
              {checking === 'partial' ? 'Checking…' : 'Check what will be removed'}
            </button>
            <span className="formactions__spacer" />
            <button
              type="button"
              className="btn btn--danger"
              disabled={!partialPreview}
              title={partialPreview ? undefined : 'Check what will be removed first'}
              onClick={() => setConfirming('partial')}
            >
              Clear chosen data…
            </button>
          </div>
        )}
      </div>

      {canReset && (
        <div className="resetdata__panel resetdata__panel--amber" data-testid="reset-everything">
          <h3 className="resetdata__title">Clear everything</h3>
          <p>
            Clears every area above and puts the company settings back to the defaults. The audit log,
            user logins, roles and permissions are kept. Everyone is signed out and signs in again with
            their own password.
          </p>
          {previewFor('everything') && <PreviewSummary preview={previewFor('everything')!} />}
          <div className="formactions">
            <button
              type="button"
              className="btn"
              disabled={checking !== null}
              onClick={() => void check('everything')}
            >
              {checking === 'everything' ? 'Checking…' : 'Check what will be removed'}
            </button>
            <span className="formactions__spacer" />
            <button
              type="button"
              className="btn btn--danger"
              disabled={!previewFor('everything')}
              title={previewFor('everything') ? undefined : 'Check what will be removed first'}
              onClick={() => setConfirming('everything')}
            >
              Clear everything…
            </button>
          </div>
        </div>
      )}

      {canWipe && (
        <div className="resetdata__panel resetdata__panel--red" data-testid="reset-complete">
          <h3 className="resetdata__title">Wipe completely</h3>
          <p>
            Empties everything, including the audit log and every other login, and starts again like a
            new install. Only the built-in lists and your own login are put back. You sign in again with
            the default password and must choose a new one.
          </p>
          {previewFor('complete') && <PreviewSummary preview={previewFor('complete')!} />}
          <div className="formactions">
            <button
              type="button"
              className="btn"
              disabled={checking !== null}
              onClick={() => void check('complete')}
            >
              {checking === 'complete' ? 'Checking…' : 'Check what will be removed'}
            </button>
            <span className="formactions__spacer" />
            <button
              type="button"
              className="btn btn--danger"
              disabled={!previewFor('complete')}
              title={previewFor('complete') ? undefined : 'Check what will be removed first'}
              onClick={() => setConfirming('complete')}
            >
              Wipe completely…
            </button>
          </div>
        </div>
      )}

      {confirming && (
        <ConfirmReset
          mode={confirming}
          areas={confirming === 'partial' ? [...ticked].sort() : []}
          preview={previewFor(confirming)}
          onClose={() => setConfirming(null)}
          onDone={(r) => {
            setConfirming(null);
            done(r);
          }}
        />
      )}
    </section>
  );
}

function PreviewSummary({ preview }: { preview: ResetPreview }) {
  return (
    <div className="resetdata__preview" aria-live="polite">
      <p>
        <strong>{formatNumber(preview.total, 0)} records</strong> will be removed.
      </p>
      {preview.areas.length > 0 && (
        <ul className="resetdata__list">
          {preview.areas.map((a) => (
            <li key={a.id}>
              {a.label}: {formatNumber(a.total, 0)}
              {a.pulledIn ? ' (ticked for you)' : ''}
            </li>
          ))}
        </ul>
      )}
      <p className="settings__hint">Kept: {preview.kept.join('; ')}.</p>
      <p className="settings__hint">{preview.numbering}</p>
    </div>
  );
}

function ResetResult({ outcome, onClose }: { outcome: ResetOutcome; onClose: () => void }) {
  return (
    <div className="resetdata__result" role="status">
      <p>
        <span aria-hidden="true">✓ </span>
        {formatNumber(outcome.totalRemoved, 0)} records removed.
        {outcome.backupFile && <> Backup saved as <code>{outcome.backupFile}</code>.</>}
      </p>
      {outcome.backupWarning && (
        <p className="resetdata__warning">
          <span aria-hidden="true">⚠ </span>
          The backup could not be made: {outcome.backupWarning}
        </p>
      )}
      <button type="button" className="btn btn--ghost" onClick={onClose}>
        Close
      </button>
    </div>
  );
}

const TITLES: Record<ResetMode, string> = {
  partial: 'Clear chosen data',
  everything: 'Clear everything',
  complete: 'Wipe completely',
};

/**
 * The last step. The button stays off until the phrase is typed exactly, the
 * password is filled in and the backup box is ticked: a reset cannot be
 * undone from inside the app.
 */
export function ConfirmReset({
  mode,
  areas,
  preview,
  onClose,
  onDone,
}: {
  mode: ResetMode;
  areas: string[];
  preview: ResetPreview | null;
  onClose: () => void;
  onDone: (outcome: ResetOutcome) => void;
}) {
  const [phrase, setPhrase] = useState('');
  const [password, setPassword] = useState('');
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const wanted = PHRASES[mode];
  const ready = phrase.trim() === wanted && password.length > 0 && understood && !busy;

  async function submit() {
    setBusy(true);
    setProblem(null);
    try {
      const outcome =
        mode === 'complete'
          ? await api.post<ResetOutcome>('/api/v1/system/reset/complete', { confirm: phrase.trim(), password })
          : await api.post<ResetOutcome>('/api/v1/system/reset', {
              mode,
              ...(mode === 'partial' ? { areas } : {}),
              confirm: phrase.trim(),
              password,
            });
      onDone(outcome);
    } catch (e) {
      setProblem(errorMessage(e, 'The reset did not go through. Nothing was cleared.'));
      setBusy(false);
    }
  }

  return (
    <Modal title={TITLES[mode]} onClose={busy ? () => undefined : onClose}>
      <h2>{TITLES[mode]}</h2>
      <p>
        {preview ? <strong>{formatNumber(preview.total, 0)} records</strong> : 'Everything chosen'} will be
        removed.{' '}
        {mode === 'partial'
          ? 'You stay signed in.'
          : mode === 'everything'
            ? 'Everyone is signed out, you too.'
            : 'Everyone is signed out, you too, and every other login is removed.'}
      </p>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) void submit();
        }}
      >
        <label className="field">
          <span className="field__label">Type {wanted} to confirm</span>
          <input
            className="field__input field__input--mono"
            value={phrase}
            onChange={(e) => setPhrase(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </label>
        <label className="field">
          <span className="field__label">Your password</span>
          <input
            className="field__input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
          />
        </label>
        <label className="settings__toggle">
          <input type="checkbox" checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />
          <span>A backup is taken first. This cannot be undone from inside the app.</span>
        </label>
        {problem && <ErrorNote message={problem} />}
        <div className="formactions">
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <span className="formactions__spacer" />
          <button type="submit" className="btn btn--danger" disabled={!ready}>
            {busy ? 'Working… this can take a minute' : TITLES[mode]}
          </button>
        </div>
      </form>
    </Modal>
  );
}
